import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../App';

const label = (p) => {
  try { return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(new Date(`${p}-01T00:00:00`)); } catch { return p; }
};

export const useClosedPeriodInfo = (refreshKey) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bank-feeds/closed-period-holds`)
      .then(({ data }) => { if (alive) setInfo(data); })
      .catch(() => { if (alive) setInfo(null); });
    return () => { alive = false; };
  }, [refreshKey]);
  return info;
};

// Sep 2026 · Shown wherever bank transactions can arrive (statement upload,
// update/back-fill/re-auth of connected banks) and on the Transactions page
// while rows are held. Tells the user up-front that the books are closed
// through <month>, what happens to older rows, and links to the exact page.
export const ClosedPeriodNotice = ({ info, compact = false, className = '' }) => {
  const navigate = useNavigate();
  if (!info || (!info.closed_through && !info.held_total)) return null;
  const heldEntries = Object.entries(info.holds || {});
  return (
    <div
      className={`rounded-lg border border-amber-300 bg-amber-50 ${compact ? 'p-2.5' : 'p-3'} flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${className}`}
      data-testid="closed-period-notice"
    >
      <Lock className="w-4 h-4 text-amber-700 shrink-0" />
      <div className="text-xs text-amber-900 flex-1 min-w-0 space-y-0.5">
        {info.closed_through && (
          <p className="font-semibold text-[13px]" data-testid="closed-through-text">
            Your books are closed through {label(info.closed_through)}.
          </p>
        )}
        {info.held_total > 0 ? (
          <p data-testid="closed-period-held-text">
            <strong>{info.held_total}</strong> bank transaction{info.held_total === 1 ? ' is' : 's are'} waiting in Review Needed because {info.held_total === 1 ? 'it is' : 'they are'} dated in {heldEntries.map(([p, n]) => `${label(p)} (${n})`).join(', ')} - nothing was posted. Open the period and they will be processed automatically.
          </p>
        ) : (
          <p>Transactions dated on or before that month will be held in Review Needed (no documents, no journals) until you open the period.</p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
        onClick={() => navigate(info.period_close_url || '/period-close')}
        data-testid="open-period-cta"
      >
        Open period <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
  );
};

export default ClosedPeriodNotice;
